import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/auth-context";
import styles from "./signup.module.css";

/**
 * Where sign-up lands: the practice's own origin, spending its hand-off.
 *
 * ── Why a whole page for one exchange ───────────────────────────────────────
 *
 * Because the browser genuinely changed origin. Sign-up ran at the apex, the
 * session has to live at `cedar-dental.dentacore.app`, and `localStorage` does
 * not cross between them — so this is a real second page load, not a route
 * transition, and something has to be on screen while the token is spent.
 *
 * It is deliberately almost empty. The success case is over in a few hundred
 * milliseconds and goes straight to setup; anything more decorative would only
 * ever be seen by somebody whose token failed.
 *
 * ── The failure case is the one worth designing ─────────────────────────────
 *
 * A hand-off lives five minutes. The likely way to see it expire is closing the
 * laptop mid-redirect and coming back — at which point the practice **exists**
 * and the password **works**. So the failure is not an error to apologise for;
 * it is a signpost to the sign-in form, and it says so.
 */
export function WelcomePage(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { signInWithHandoff } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  /**
   * React runs effects twice in development's strict mode, and a hand-off is
   * single use — the second call would spend a token the first already
   * consumed and show an error on a sign-in that worked. Guarding on a ref
   * rather than on state, because state updates are not synchronous enough to
   * stop the second run.
   */
  const spent = useRef(false);

  useEffect(() => {
    if (token === undefined || spent.current) return;
    spent.current = true;

    signInWithHandoff(token)
      .then(() => navigate("/setup", { replace: true }))
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      });
  }, [token, signInWithHandoff, navigate]);

  if (error !== null) {
    return (
      <main className={styles.page}>
        <h1>Your practice is ready</h1>
        <p role="alert" data-testid="welcome-error">
          {error}
        </p>
        <p>
          Nothing is wrong with the account — this link is just a one-time shortcut and it has been
          used or has expired. <Link to="/login">Sign in</Link> with the email and password you
          chose.
        </p>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <h1>Setting up your practice</h1>
      <p className={styles.lede}>One moment.</p>
    </main>
  );
}
